import { Configuration } from '@icc/configurator/shared';
import { PriceSegment, DiscountGroups, PricePart } from '@icc/shared/types/price/Prices';
import {
    RollerMontageType,
    RollerEndRail,
    RollerProfile,
    RollerShutterType,
    TimeLimitSegment,
    RollerCover,
    RoundReel,
    BoxDepthExtension,
    AdditionalPolystyreneInsert,
    RollerAdapter,
    HorizontalBoxReinforcement,
    StaticBracket,
    SteelMountingAnchor,
    SteelSideBracket,
    MountingBracket,
    BoxWidthExtension,
    InspectionHatchSupportBracket,
    InspectionHatch,
    PlasterStrip,
    PlasterStripExtension,
    BoxSideInsulation,
    RollerBoxSideCap,
    RollerGuides,
    RollerDimensioningType,
    StaticBracketAmount,
    RollerGuidesSpacerStrips,
    RollerGuidesCaps,
    RollerGuidesOverlays,
    RollerGuidesChannelCaps,
    RollerGuidesDrillings,
    RollerGuidesExtensions,
    RollerMosquitoScreenType,
    RollerBlindSide,
    RollerDrive,
    RollerWeight,
    RollerRemoteControl,
    RollerTapeCordOutput,
    Packaging,
} from '@icc/shared/types';
import { core } from '@icc/shared';

import { RollerShutterActiveConfiguration } from './RollerShutterActiveConfiguration';
import { Layout } from '../types/layout';
import { RollerShutter } from '../types/roller-shutter';
import { CoverSegment } from '../types/cover-segment';
import { InspectionHatchLocation } from '../types/inspection-hatch-location';
import { RollerMosquitoScreen } from '../types/mosquito-screen';
import { Color, ColorsConfig } from '../types/color';
import { TransProp } from '@icc/shared/helpers';
/**
 * Konfiguracja żaluzji zewnętrznej
 */
export class RollerShutterConfiguration implements Configuration {
    $version = 20;
    type: 'roller_shutter' = 'roller_shutter';
    departments: any = {};
    name = '';
    price: number | null = null;
    priceNoMargin: number | null = null;
    priceAfterMultiplier: number | null = null;
    priceAfterMultiplierNoMargin: number | null = null;
    priceAfterDiscounts: number | null = null;
    priceIsGross: boolean | null = null;
    priceTaxRate: number | null = null;
    quantity = 1;
    priceSegments: PriceSegment[] = [];
    priceSegmentsNoMargin: PriceSegment[] = [];
    priceParts: PricePart[] = [];
    pricePartsNoMargin: PricePart[] = [];
    discountGroups: DiscountGroups = {};
    discountGroupsNoMargin: DiscountGroups = {};
    pricesRevisions: {
        prices: number;
        priceRasters: number;
        charge: number;
        multiplier: number;
        marketFactor: number;
    } = {
        prices: 0,
        priceRasters: 0,
        charge: 0,
        multiplier: 0,
        marketFactor: 0,
    };
    timeLimit: number | null = null;
    timeLimitsStack: TimeLimitSegment[] = [];
    title = '';
    description = '';
    /** Załączniki
     *
     * *Aktualnie niewykorzystywane*
     */
    attachments: any[] = [];
    /** Usunięte załączniki
     *
     * *Aktualnie niewykorzystywane*
     */
    deletedAttachments: any[] = [];
    valid: {} = {};
    attributes: {
        id: string;
        name: string;
        value: string;
        valueId?: number;
    }[] = [];

    modified = new Date().toString();

    /**
     * Słownik z dodatkowymi danymi składowych konfiguracji
     */
    dictionary: {
        accessoryCategories: Record<number, {
            id: number;
            name: string;
        }>;
    } = {
        accessoryCategories: {},
    };

    private converters = {};

    /** Typ produktu */
    @TransProp()
    shutterType: RollerShutterType;
    /** Przystosowana do typu */
    @TransProp()
    adaptedForShutterType: RollerShutterType | null = null;
    /** Metoda wymiarowania: `shutter` - wymiar żaluzji, `hole` - wymiar otworu, `window` - wymiar okna */
    dimensioningType: RollerDimensioningType = 'window';
    /** Rolety */
    shutters: RollerShutter[] = [];

    width = 0;
    height = 0;

    /** Typ pancerza */
    @TransProp()
    profile: RollerProfile;

    manuallySelectedProfile: RollerProfile | null = null;

    /** Rodzaj perforacji lameli */
    perforationType: 'all_perforated' | 'selected_non_perforated' | 'all_non_perforated' = 'all_perforated';
    /** Liczba nieperforowanych lameli (od góry) */
    nonPerforatedSlatsCount: number | null = null;
    /** Czy perforacja była zmieniana ręcznie przez użytkownika */
    perforationManuallyChanged: boolean = false;

    /** Listwa dolna */
    @TransProp()
    endRail: RollerEndRail | null = null;
    /** Czy bufor listwy dolnej występuje w konfiguracji */
    endRailBufferEnabled = false;
    /** Typ montażu */
    @TransProp()
    montageType: RollerMontageType;
    /** Układ rolet */
    layout: Layout;
    /** Skrzynki */
    coverSegments: CoverSegment[];
    /** Czy długości skrzynki zmieniane przez użytkownika */
    defaultCoverSegments = true;
    /** Skrzynka */
    @TransProp()
    cover: RollerCover;
    /** Wysokość skrzynki */
    coverHeight: {
        /** Wysokość [mm] */
        height: number;
        /** Wysokość [mm] */
        defaultHeight: number;
        /** Głębokość [mm] */
        depth: number;
        /** Głębokość [mm] */
        defaultDepth: number;
        /** Czy wybrane automatycznie */
        isDefault: boolean;
    } = {
        height: 0,
        defaultHeight: 0,
        depth: 0,
        defaultDepth: 0,
        isDefault: true,
    };
    /** Rura nawojowa */
    @TransProp()
    roundReel: RoundReel | null = null;
    /** Miejsce klapy rewizyjnej */
    inspectionHatchLocation: InspectionHatchLocation = 'bottom';
    /** Pogłębienie skrzynki */
    @TransProp()
    boxDepthExtension: BoxDepthExtension | null = null;
    /** Dodatkowa wkładka styropianowa */
    @TransProp()
    additionalPolystyreneInsert: AdditionalPolystyreneInsert | null = null;
    /** Flaga oznaczająca interakcję użytkownika z dodatkową wkładką styropianową: null = brak interakcji, true = ręcznie dodana, false = ręcznie usunięta */
    additionalPolystyreneInsertManuallyAdded: boolean | null = null;
    /** Adapter okienny */
    @TransProp()
    rollerBoxAdapter: RollerAdapter | null = null;

    /** Wzmocnienia skrzynki poziome */
    @TransProp(HorizontalBoxReinforcement)
    horizontalBoxReinforcements: HorizontalBoxReinforcement[] = [];

    /** Konsole statyczne */
    @TransProp()
    staticBrackets: StaticBracket | null = null;
    /** Ilość konsol statycznych */
    staticBracketsAmount: StaticBracketAmount = {
        amount: 0,
        isDefault: true,
    };
    /** Stalowe kotwy montażowe */
    @TransProp()
    steelMountingAnchors: SteelMountingAnchor | null = null;
    /** Stalowe uchwyty boczne */
    @TransProp(SteelSideBracket)
    steelSideBrackets: SteelSideBracket[] = [];
    /** Uchwyt montażowy */
    @TransProp()
    mountingBrackets: MountingBracket | null = null;
    /** Poszerzenie */
    boxWidthExtensions: BoxWidthExtension[] = [];
    /** Wartości poszerzenia skrzynki wprowadzone przez użytkownika */
    boxWidthExtensionValues: {
        marginLeft: number;
        marginBetween1and2: number;
        marginBetween2and3: number;
        marginRight: number;
    } = {
        marginLeft: 0,
        marginBetween1and2: 0,
        marginBetween2and3: 0,
        marginRight: 0,
    };
    /** Wspornik klapy rewizyjnej */
    @TransProp()
    inspectionHatchSupportBrackets: InspectionHatchSupportBracket | null = null;
    /** Klapa rewizyjna */
    @TransProp()
    inspectionHatch: InspectionHatch | null = null;
    /** Listwa tynkowa (wewnętrzna/zewnętrzna) */
    @TransProp()
    plasterStrip: PlasterStrip | null = null;
    /** Listwa tynkowa zewnętrzna */
    @TransProp()
    outerPlasterStrip: PlasterStrip | null = null;
    /** Listwa tynkowa wewnętrzna */
    @TransProp()
    innerPlasterStrip: PlasterStrip | null = null;
    /** Flaga oznaczająca czy użytkownik ręcznie usunął zewnętrzną listwę tynkową */
    outerPlasterStripManuallyRemoved = false;
    /** Flaga oznaczająca czy użytkownik ręcznie usunął wewnętrzną listwę tynkową */
    innerPlasterStripManuallyRemoved = false;
    /** Przedłużenie listwy tynkowej */
    @TransProp()
    plasterStripExtension: PlasterStripExtension | null = null;

    /** Izolacja boków skrzynki */
    @TransProp()
    boxSideInsulation: BoxSideInsulation | null = null;

    /** Zaślepki boków skrzynki */
    @TransProp()
    boxSideCap: RollerBoxSideCap | null = null;
    /** Dodatkowe opakowanie */
    @TransProp()
    packaging: Packaging | null = null;

    /** Prowadnice */
    @TransProp(RollerGuides)
    guides: RollerGuides[] = [];
    @TransProp(RollerGuidesChannelCaps)
    guidesChannelCaps: RollerGuidesChannelCaps[] = [];

    @TransProp(RollerGuidesOverlays)
    guidesOverlays: RollerGuidesOverlays[] = [];
    @TransProp(RollerGuidesCaps)
    guidesCaps: RollerGuidesCaps[] = [];
    @TransProp(RollerGuidesSpacerStrips)
    guidesSpacerStrips: RollerGuidesSpacerStrips[] = [];
    @TransProp(RollerGuidesDrillings)
    guidesDrillings: RollerGuidesDrillings[] = [];
    guidesCuttingAngle: number | null = null;
    guidesExtensions: RollerGuidesExtensions[] = [];
    /** Czy zatyczki/zaślepki prowadnic są włączone */
    toggledGuidesCaps: {
        toggled: boolean;
        isDefault: boolean;
    } = { toggled: false, isDefault: true };

    /** Moskitiery */
    mosquitoScreens: RollerMosquitoScreen[] = [];
    /** Stan moskitier oczekiwany przez użytkownika w poszczególnych skrzynkach/roletach */
    mosquitoScreenExpectation: Record<string, boolean | null> = {};
    /** Rodzaj moskitiery */
    @TransProp(RollerMosquitoScreenType)
    mosquitoScreenType: RollerMosquitoScreenType | null = null;

    /** Boki skrzynki */
    @TransProp()
    selectedSides: RollerBlindSide | null;

    /** Kolorystyka */
    @TransProp()
    colors: ColorsConfig;

    /** Sterowanie */
    @TransProp(RollerDrive)
    drives: RollerDrive[] = [];

    /** Piloty sterowania */
    @TransProp(RollerRemoteControl)
    remoteControls: RollerRemoteControl[] = [];

    /** Waga */
    weight: RollerWeight = {
        endRail: {
            all: 0
        },
        profile: {
            all: 0
        },
        all: 0
    };

    selectedTapeCordOutput: RollerTapeCordOutput | null = null;

    constructor(configuration?, dataRequiredToUpdate?: any, conversion = true) {
        if (RollerShutterConfiguration.is(configuration) && conversion) {
            this.runConversion(configuration, dataRequiredToUpdate);
        } else {
            if (RollerShutterActiveConfiguration.is(configuration)) {
                this.name = configuration.Name;
                this.departments = configuration.departments;
                this.price = configuration.Price;
                this.priceNoMargin = configuration.PriceNoMargin || configuration.Price;
                this.priceAfterMultiplier =
                    configuration.PriceAfterMultiplier || configuration.Price;
                this.priceAfterMultiplierNoMargin =
                    configuration.PriceAfterMultiplierNoMargin || configuration.PriceNoMargin;
                this.priceAfterDiscounts = configuration.PriceAfterDiscounts || configuration.Price;
                this.priceIsGross = configuration.PriceIsGross || false;
                this.priceTaxRate = configuration.PriceTaxRate || 0;
                this.quantity = configuration.Quantity;
                this.priceSegments = configuration.PriceSegments;
                this.priceSegmentsNoMargin = configuration.PriceSegmentsNoMargin;
                this.priceParts = configuration.PriceParts;
                this.pricePartsNoMargin = configuration.PricePartsNoMargin;
                this.discountGroups = configuration.DiscountGroups;
                this.discountGroupsNoMargin = configuration.DiscountGroupsNoMargin;
                this.pricesRevisions = {
                    prices: configuration.PricesRevisions?.prices || 0,
                    priceRasters: configuration.PricesRevisions?.priceRasters || 0,
                    charge: configuration.PricesRevisions?.charge || 0,
                    multiplier: configuration.PricesRevisions?.multiplier || 0,
                    marketFactor: configuration.PricesRevisions?.marketFactor || 0,
                };
                this.title = configuration.Title;
                this.description = configuration.Description;
                this.attachments = configuration.Attachments;
                this.valid = configuration.valid;
                this.timeLimit = configuration.timeLimit;
                this.timeLimitsStack = configuration.timeLimitsStack;
                this.attributes = configuration.attributes;

                this.shutterType = configuration.shutterType;
                this.adaptedForShutterType = configuration.adaptedForShutterType;
                this.profile = configuration.profile;
                this.manuallySelectedProfile = configuration.manuallySelectedProfile;

                this.perforationType = configuration.perforationType || 'all_perforated';
                this.nonPerforatedSlatsCount = configuration.nonPerforatedSlatsCount;
                this.perforationManuallyChanged = configuration.perforationManuallyChanged || false;

                this.endRail = configuration.endRail;
                this.endRailBufferEnabled =
                    configuration.endRailBufferEnabled ??
                    !!configuration.shutterType?.endRailBufferColorChoice;
                this.horizontalBoxReinforcements = configuration.horizontalBoxReinforcements || [];

                this.dimensioningType = configuration.dimensioningType;
                this.shutters = configuration.shutters;
                this.montageType = configuration.montageType;

                this.layout = configuration.layout;
                this.coverSegments = configuration.coverSegments;
                this.defaultCoverSegments = configuration.defaultCoverSegments;
                this.cover = configuration.cover;
                this.coverHeight = configuration.coverHeight;

                this.roundReel = configuration.roundReel;
                this.inspectionHatchLocation = configuration.inspectionHatchLocation;
                this.boxDepthExtension = configuration.boxDepthExtension;
                this.additionalPolystyreneInsert = configuration.additionalPolystyreneInsert;
                this.additionalPolystyreneInsertManuallyAdded = configuration.additionalPolystyreneInsertManuallyAdded || false;
                this.rollerBoxAdapter = configuration.rollerBoxAdapter;

                this.staticBrackets = configuration.staticBrackets;
                this.staticBracketsAmount = configuration.staticBracketsAmount || {
                    amount: 0,
                    isDefault: true,
                };
                this.steelMountingAnchors = configuration.steelMountingAnchors || null;
                this.steelSideBrackets = configuration.steelSideBrackets || [];
                this.mountingBrackets = configuration.mountingBrackets;
                this.boxWidthExtensions = configuration.boxWidthExtensions || [];
                this.boxWidthExtensionValues = configuration.boxWidthExtensionValues || {
                    marginLeft: 0,
                    marginBetween1and2: 0,
                    marginBetween2and3: 0,
                    marginRight: 0,
                };
                this.inspectionHatchSupportBrackets = configuration.inspectionHatchSupportBrackets;
                this.inspectionHatch = configuration.inspectionHatch;

                this.plasterStrip = configuration.plasterStrip;
                this.outerPlasterStrip = configuration.outerPlasterStrip;
                this.innerPlasterStrip = configuration.innerPlasterStrip;
                this.outerPlasterStripManuallyRemoved = configuration.outerPlasterStripManuallyRemoved;
                this.innerPlasterStripManuallyRemoved = configuration.innerPlasterStripManuallyRemoved;

                this.plasterStripExtension = configuration.plasterStripExtension;

                this.boxSideInsulation = configuration.boxSideInsulation;
                this.boxSideCap = configuration.boxSideCap;

                this.packaging = configuration.packaging || null;

                this.guides = configuration.guides;
                this.guidesOverlays = configuration.guidesOverlays || [];
                this.guidesCaps = configuration.guidesCaps || [];
                this.guidesSpacerStrips = configuration.guidesSpacerStrips || [];
                this.guidesChannelCaps = configuration.guidesChannelCaps || [];
                this.guidesDrillings = configuration.guidesDrillings || [];
                this.guidesCuttingAngle = configuration.guidesCuttingAngle || null;
                this.guidesExtensions = configuration.guidesExtensions || [];
                this.toggledGuidesCaps = configuration.toggledGuidesCaps || { toggled: false, isDefault: true };

                this.mosquitoScreens = configuration.mosquitoScreens;
                this.mosquitoScreenExpectation = configuration.mosquitoScreenExpectation;
                this.mosquitoScreenType = configuration.mosquitoScreenType;

                this.selectedSides = configuration.selectedSides;

                this.colors = configuration.colors;

                this.drives = configuration.drives;
                this.remoteControls = configuration.remoteControls;

                // Populate dictionary with accessory categories
                this.populateAccessoryCategories(configuration);
                this.weight = configuration.weight;
                this.width = configuration.width;
                this.height = configuration.height;

                this.selectedTapeCordOutput = configuration.selectedTapeCordOutput;

                if (!this.endRailBufferEnabled) {
                    delete this.colors.endRailBuffer;
                }
            }
        }
    }

    public static is(configuration): configuration is RollerShutterConfiguration {
        return configuration instanceof RollerShutterConfiguration || configuration.$version;
    }

    protected runConversion(configuration, dataRequiredToUpdate: any) {
        let updatedConfiguration = core.copy(configuration);

        for (let version = configuration.$version + 1; version <= this.$version; version++) {
            updatedConfiguration.$version = version;
            if (this.converters[version] && typeof this.converters[version] === 'function') {
                updatedConfiguration = this.converters[version](
                    updatedConfiguration,
                    dataRequiredToUpdate
                );
            }
        }
        (Object as any)
            .entries(updatedConfiguration)
            .forEach(([key, value]) => (this[key] = value));
    }

    /**
     * Populate dictionary with accessory categories from all accessories
     */
    private populateAccessoryCategories(configuration: RollerShutterActiveConfiguration) {
        const addCategory = (item: { categoryId?: number; categoryName?: string } | null | undefined) => {
            if (item?.categoryId) {
                this.dictionary.accessoryCategories[item.categoryId] = {
                    id: item.categoryId,
                    name: item.categoryName || '',
                };
            }
        };

        const addCategoryFromArray = (items: { categoryId?: number; categoryName?: string }[] | null | undefined) => {
            items?.forEach(item => addCategory(item));
        };

        // Single accessories
        addCategory(configuration.staticBrackets);
        addCategory(configuration.steelMountingAnchors);
        addCategory(configuration.mountingBrackets);
        addCategory(configuration.inspectionHatchSupportBrackets);
        addCategory(configuration.plasterStripExtension);
        addCategory(configuration.boxSideInsulation);
        addCategory(configuration.boxSideCap);
        addCategory(configuration.packaging);
        addCategory(configuration.mosquitoScreenType);

        // Array accessories
        addCategoryFromArray(configuration.steelSideBrackets);
        addCategoryFromArray(configuration.boxWidthExtensions);
        addCategoryFromArray(configuration.remoteControls);

        // Guide overlay snap plates from guidesOverlays
        configuration.guidesOverlays?.forEach(overlay => {
            addCategory(overlay.left?.snapPlate);
            addCategory(overlay.right?.snapPlate);
            overlay.mosquitoScreens?.forEach(ms => {
                addCategory(ms.overlay?.snapPlate);
            });
        });

        // Guide spacer strip snap plates from guidesSpacerStrips
        configuration.guidesSpacerStrips?.forEach(spacerStrip => {
            addCategory(spacerStrip.left?.snapPlate);
            addCategory(spacerStrip.right?.snapPlate);
            spacerStrip.mosquitoScreens?.forEach(ms => {
                addCategory(ms.spacerStrip?.snapPlate);
            });
        });

        // Block hangers and security bolts from drives
        configuration.drives?.forEach(drive => {
            addCategory(drive.blockHanger);
            addCategory(drive.securityBolt);
        });
    }
}

export type IRollerShutterConfiguration = Pick<
    RollerShutterConfiguration,
    keyof RollerShutterConfiguration
>;
